import { useMemo, useState } from 'react';
import type { AriStateDay, CalendarDayCell } from '@/shared/api';
import { CalendarLegend } from './CalendarLegend';
import { DayCell } from './DayCell';
import { MonthHeader } from './MonthHeader';
import { WEEKDAYS, addMonths, buildMonthGrid, firstOfMonth } from './calendarHelpers';

interface PriceCalendarProps {
  bookingCellsByDate: Map<string, CalendarDayCell>;
  ariDays?: AriStateDay[];
  defaultRate: number;
  currency: string;
  /** Fehlt bei View-only; die Zellen bleiben dann reine Lesefläche. */
  onDayClick?: (date: string, state: AriStateDay | null) => void;
}

export function PriceCalendar({
  bookingCellsByDate,
  ariDays = [],
  defaultRate,
  currency,
  onDayClick,
}: PriceCalendarProps) {
  const [cursor, setCursor] = useState(() => firstOfMonth(new Date()));
  const ariByDate = useMemo(() => new Map(ariDays.map((day) => [day.date, day])), [ariDays]);

  const days = useMemo(
    () => buildMonthGrid(cursor, ariByDate, bookingCellsByDate, defaultRate),
    [cursor, ariByDate, bookingCellsByDate, defaultRate],
  );

  return (
    <div className="space-y-3">
      <MonthHeader
        cursor={cursor}
        onPrev={() => setCursor(addMonths(cursor, -1))}
        onNext={() => setCursor(addMonths(cursor, 1))}
        onToday={() => setCursor(firstOfMonth(new Date()))}
      />
      <div className="text-ink-faint grid grid-cols-7 gap-1.5 text-center text-[10px] font-semibold uppercase tracking-[0.14em]">
        {WEEKDAYS.map((wd) => (
          <div key={wd}>{wd}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {days.map((d) => (
          <DayCell
            key={d.date}
            day={d}
            currency={currency}
            onClick={
              onDayClick ? () => onDayClick(d.date, ariByDate.get(d.date) ?? null) : undefined
            }
          />
        ))}
      </div>
      <CalendarLegend
        currency={currency}
        defaultRate={defaultRate}
        closedByFamily={days.some((day) => day.closedByFamily)}
        doubleBooked={days.some((day) => day.doubleBooked)}
      />
    </div>
  );
}
